import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { toWeeks, useGitHubContributions } from "../../hooks/useGitHubContributions";
import "./GitHubStats.css";

interface Props {
    username: string;
}

function Counter({ to, suffix = "" }: { to: number; suffix?: string }) {
    const ref = useRef<HTMLSpanElement>(null);
    const inView = useInView(ref, { once: true, margin: "-20%" });
    const [val, setVal] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, to, {
            duration: 1.6,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setVal(Math.floor(v)),
        });
        return () => controls.stop();
    }, [inView, to]);

    return (
        <span ref={ref}>
            {val.toLocaleString()}
            {suffix}
        </span>
    );
}

export default function GitHubStats({ username }: Props) {
    const { data, total, loading, error } = useGitHubContributions(username);
    const heatRef = useRef<HTMLDivElement>(null);

    // On a narrow card the year doesn't fit: start scrolled to the newest weeks.
    useEffect(() => {
        const el = heatRef.current;
        if (el) el.scrollLeft = el.scrollWidth;
    }, [data, loading]);

    // 53 weeks × 7 days: the last year of data.
    const weeks = toWeeks(data, 53);

    // Compute streak from real data, fallback if needed
    const currentStreak = (() => {
        if (!data) return 12;
        let s = 0;
        for (let i = data.length - 1; i >= 0; i--) {
            if (data[i].count > 0) s++;
            else break;
        }
        return s;
    })();

    return (
        <div className="gh-stats">
            <div className="gh-stats-head">
                <span className="bento-label">GitHub · last 12 months</span>
                <a
                    href={`https://github.com/${username}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bento-link mono"
                >
                    @{username} ↗
                </a>
            </div>

            <div className="gh-numbers">
                <div className="gh-stat">
                    <span className="gh-stat-value mono">
                        <Counter to={total} />
                    </span>
                    <span className="gh-stat-label">contributions</span>
                </div>
                <div className="gh-stat">
                    <span className="gh-stat-value mono">
                        <Counter to={currentStreak} />
                    </span>
                    <span className="gh-stat-label">day streak</span>
                </div>
            </div>

            <div ref={heatRef} className="gh-heatmap" role="img" aria-label="GitHub contribution heatmap">
                {loading && !error && (
                    <div className="gh-skeleton">
                        {Array.from({ length: 53 * 7 }).map((_, i) => (
                            <div key={i} className="gh-cell gh-cell--skel" />
                        ))}
                    </div>
                )}
                {!loading && weeks.length > 0 && (
                    <div className="gh-grid">
                        {weeks.map((week, wi) => (
                            <div key={wi} className="gh-week">
                                {Array.from({ length: 7 }).map((_, di) => {
                                    const day = week[di];
                                    if (!day) return <div key={di} className="gh-cell gh-cell--empty" />;
                                    return (
                                        <motion.div
                                            key={di}
                                            className={`gh-cell gh-cell--l${day.level}`}
                                            initial={{ opacity: 0, scale: 0.6 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            transition={{
                                                duration: 0.35,
                                                delay: (wi * 7 + di) * 0.002,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            title={`${day.count} contributions on ${day.date}`}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>
                )}
                {error && (
                    <div className="gh-error mono">
                        Live data unavailable · showing placeholder
                    </div>
                )}
            </div>

            <div className="gh-legend mono">
                <span>Less</span>
                <span className="gh-cell gh-cell--l0" />
                <span className="gh-cell gh-cell--l1" />
                <span className="gh-cell gh-cell--l2" />
                <span className="gh-cell gh-cell--l3" />
                <span className="gh-cell gh-cell--l4" />
                <span>More</span>
            </div>
        </div>
    );
}
